import { KeyRound, Lock, UserCircle2 } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import { ImageField } from "@/components/ImageField";
import {
  Button,
  Card,
  ErrorBanner,
  InfoBanner,
  Input,
  PageTitle,
  Select,
  Spinner,
} from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useT, type Chave } from "@/lib/i18n";
import { cpfMask, isValidCpf } from "@/lib/format";
import { useChangePassword, useMyProfile, useMyProfileUpdate } from "@/lib/queries";
import type { Me, PixKeyType } from "@/lib/types";

const PIX_LABEL: Record<Exclude<PixKeyType, "">, Chave | string> = {
  cpf: "CPF",
  phone: "profile.pixPhone",
  email: "common.email",
  random: "profile.randomKey",
};

/**
 * O cadastro do colaborador, editado por ele mesmo.
 *
 * Existe porque o caminho anterior era: profissional pede ao dono, dono
 * digita errado, o Pix do fechamento volta. Quem recebe o dinheiro é
 * quem deve manter a chave — e responder por ela, já que toda troca fica
 * registrada e o dono consegue ver.
 *
 * A comissão aparece aqui em modo leitura de propósito: o profissional
 * precisa saber quanto ganha (docs/03 §4) sem poder mudar.
 */
export default function MyProfile() {
  const t = useT();
  const me = useOutletContext<Me>();
  const { data: perfil, isLoading, isError, error } = useMyProfile();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-8" />
      </div>
    );
  }

  // Recepção e gerente não têm perfil de atendimento — a tela precisa
  // dizer isso, não mostrar um formulário vazio que não salva.
  if (isError || !perfil) {
    const semPerfil = (error as { response?: { status?: number } } | null)?.response?.status === 404;
    return (
      <div className="p-4 lg:p-8">
        <PageTitle title={t("profile.title")} />
        <Card className="p-6">
          <InfoBanner>
            {semPerfil
              ? t("profile.noProfileText")
              : t("profile.loadFailed")}
          </InfoBanner>
        </Card>
        <SenhaCard className="mt-4" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      <PageTitle
        title={t("profile.title")}
        subtitle={t("profile.commissionOf", {
          shop: me.garage.name,
          n: perfil.commission_percent,
        })}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <CadastroCard />
        <div className="space-y-4">
          {me.garage.region.payout_method === "pix" && <RepasseCard />}
          <SenhaCard />
        </div>
      </div>
    </div>
  );
}

function CadastroCard() {
  const t = useT();
  const { data: perfil } = useMyProfile();
  const salvar = useMyProfileUpdate();
  const [nome, setNome] = useState(perfil?.display_name ?? "");
  const [bio, setBio] = useState(perfil?.bio ?? "");
  const [cor, setCor] = useState(perfil?.color ?? "#2563eb");
  const [foto, setFoto] = useState<File | null>(null);
  const [ok, setOk] = useState(false);

  const erro = salvar.isError ? toApiError(salvar.error) : null;

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    setOk(false);
    salvar.mutate(
      { display_name: nome, bio, color: cor, ...(foto ? { photo: foto } : {}) },
      {
        onSuccess: () => {
          setFoto(null);
          setOk(true);
        },
      },
    );
  };

  return (
    <Card className="p-5">
      <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold">
        <UserCircle2 size={18} className="text-racing-500" />{t("profile.howYouAppear")}</h2>
      <form onSubmit={enviar} className="space-y-4">
        <ImageField
          label={t("profile.photo")}
          hint={t("profile.photoHint")}
          shape="circle"
          value={perfil?.photo ?? null}
          file={foto}
          onPick={setFoto}
        />
        <Input label={t("profile.displayName")} required value={nome} onChange={(e) => setNome(e.target.value)} />
        <label className="block">
          <span className="eyebrow mb-1.5 block text-steel-dim">
            {t("profile.bio")} <span className="normal-case tracking-normal">({t("common.optional")})</span>
          </span>
          <textarea
            rows={3}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t("team.bioPlaceholder")}
            className="w-full rounded-[4px] border border-line bg-carbon-900 px-3 py-2.5 text-steel outline-none transition placeholder:text-steel-dim focus:border-nitro-500"
          />
        </label>
        <Input
          label={t("team.agendaColor")}
          type="color"
          className="h-10 p-1"
          value={cor}
          onChange={(e) => setCor(e.target.value)}
        />

        <ErrorBanner message={erro?.message} />
        {ok && !erro && <p className="text-sm text-emerald-400">{t("profile.saved")}</p>}

        <Button type="submit" className="w-full" disabled={salvar.isPending}>
          {salvar.isPending ? <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" /> : t("common.save")}
        </Button>
      </form>
    </Card>
  );
}

/** CPF e chave Pix — a ficha de repasse, que é brasileira inteira.
 *
 *  Quem chama decide se ela aparece: ver `region.payout_method`. */
function RepasseCard() {
  const t = useT();
  const { data: perfil } = useMyProfile();
  const salvar = useMyProfileUpdate();
  const [cpf, setCpf] = useState(cpfMask(perfil?.document ?? ""));
  const [tipo, setTipo] = useState<PixKeyType>(perfil?.pix_key_type ?? "");
  const [chave, setChave] = useState(perfil?.pix_key ?? "");
  const [ok, setOk] = useState(false);

  const apiErro = salvar.isError ? toApiError(salvar.error) : null;
  // Validação local antes de gastar uma ida ao servidor — o CPF é
  // digitado no celular, entre um serviço e outro.
  const cpfInvalido = cpf.replace(/\D/g, "").length > 0 && !isValidCpf(cpf);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (cpfInvalido) return;
    setOk(false);
    salvar.mutate(
      { document: cpf.replace(/\D/g, ""), pix_key_type: tipo, pix_key: chave },
      { onSuccess: () => setOk(true) },
    );
  };

  return (
    <Card className="p-5">
      <h2 className="mb-1 flex items-center gap-2 font-display text-lg font-bold">
        <Lock size={18} className="text-racing-500" />
        {t("profile.payoutTitle")}
      </h2>
      <p className="mb-4 text-sm text-steel-muted">{t("profile.payoutHint")}</p>
      <form onSubmit={enviar} className="space-y-4">
        <Input
          label="CPF"
          inputMode="numeric"
          placeholder="000.000.000-00"
          value={cpf}
          onChange={(e) => setCpf(cpfMask(e.target.value))}
          error={cpfInvalido ? t("team.taxIdInvalid", { documento: "CPF" }) : undefined}
        />
        <Select
          label={t("profile.pixKeyType")}
          value={tipo}
          onChange={(e) => setTipo(e.target.value as PixKeyType)}
        >
          <option value="">{t("profile.notSet")}</option>
          {Object.entries(PIX_LABEL).map(([valor, rotulo]) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </Select>
        {tipo && (
          <Input
            label={t("profile.pixKey")}
            value={chave}
            onChange={(e) => setChave(e.target.value)}
            placeholder={tipo === "email" ? "voce@email.com" : undefined}
          />
        )}

        <ErrorBanner message={apiErro?.message} />
        {ok && !apiErro && <p className="text-sm text-emerald-400">{t("profile.payoutSaved")}</p>}

        <Button type="submit" className="w-full" disabled={salvar.isPending || cpfInvalido}>
          {salvar.isPending ? <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" /> : t("common.save")}
        </Button>
      </form>
    </Card>
  );
}

export function SenhaCard({ className }: { className?: string }) {
  const t = useT();
  const trocar = useChangePassword();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [ok, setOk] = useState(false);

  const erro = trocar.isError ? toApiError(trocar.error) : null;

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    setOk(false);
    trocar.mutate(
      { current_password: atual, new_password: nova },
      {
        onSuccess: () => {
          setAtual("");
          setNova("");
          setOk(true);
        },
      },
    );
  };

  return (
    <Card className={className ? `p-5 ${className}` : "p-5"}>
      <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold">
        <KeyRound size={18} className="text-racing-500" />
        {t("profile.changePassword")}
      </h2>
      <form onSubmit={enviar} className="space-y-4">
        <Input
          label={t("profile.currentPassword")}
          type="password"
          autoComplete="current-password"
          required
          value={atual}
          onChange={(e) => setAtual(e.target.value)}
          error={erro?.field === "current_password" ? erro.message : undefined}
        />
        <Input
          label={t("auth.newPassword")}
          type="password"
          autoComplete="new-password"
          required
          value={nova}
          onChange={(e) => setNova(e.target.value)}
          error={erro?.field === "new_password" ? erro.message : undefined}
        />
        {erro && !erro.field && <ErrorBanner message={erro.message} />}
        {ok && <p className="text-sm text-emerald-400">{t("profile.passwordChanged")}</p>}
        <Button type="submit" variant="secondary" className="w-full" disabled={trocar.isPending}>
          {trocar.isPending ? <Spinner className="size-4" /> : t("profile.changePassword")}
        </Button>
      </form>
    </Card>
  );
}
